import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

// Updated custom hook paths
import moment from "moment";
import Actions from "../../components/Common/Actions";
import AntdBreadcrumbs from "../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../components/Common/Buttons/FilledButton";
import DoctorsFilterOptions from "../../components/Common/CommonFilterOptions";
import ListPageLayout from "../../components/Layout";
import Deletepopup from "../../components/Modal/Deletepopup";
import useDebouncedSearch from "../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../components/Utils/hooks/useSortHandler";

// const getsrcValue = (value) => {
//   if (typeof value === "string" && value.length)
//     return process.env.REACT_APP_API_URL + "pharmacy-id-images/" + value;
//   return typeof value === "object" ? URL.createObjectURL(value) : " ";
// };

const getsrcValue = (value) => {
  if (!value) return "";

  // ✅ Already a full URL (DigitalOcean, CDN, etc.)
  if (
    typeof value === "string" &&
    (value.startsWith("http://") || value.startsWith("https://"))
  ) {
    return value;
  }

  // ✅ Local filename (stored in backend /pharmacy-id-images/)
  if (typeof value === "string" && value.length) {
    return `${process.env.REACT_APP_API_URL}pharmacy-id-images/${value}`;
  }

  // ✅ New upload (File object)
  if (typeof value === "object") {
    return URL.createObjectURL(value);
  }

  return "";
};

const PharmaciesPartnerID = () => {
  const navigate = useNavigate();
  const addNewPharmacyPartnerID = {
    id: "addnewpost",
    onClick: () => navigate(`/rp-qr-ratings/add-new-pharmacy-partner-id`),
    style: { width: "240px" },
    label: "Add New Pharmacy Partner ID",
  };
  // Sorting
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);
  const [loader, setLoader] = useState(true);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }))
  );

  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}rp-qr-ratings/list`,
      initialParams: {
        sort,
        searchText: search,
      },
    });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "rp-qr-ratings",
    fetchData,
    setShowDelete
  );

  useEffect(() => {
    setLoader(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search]);

  const columns = [
    {
      title: "S.No",
      key: "sno",
      render: (_, __, index) => Number(pagination.skip || 0) + (index + 1),
      fixed: "left",
      width: 80,
    },
    {
      title: "Pharmacy ID",
      dataIndex: "pharmacy_id",
      key: "pharmacy_id",
      fixed: "left",
      width: 220,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      width: 220,
    },
    {
      title: "Time of Registration",
      dataIndex: "createdAt",
      key: "time",
      render: (createdAt) => moment(createdAt).format("LT"),
      width: 250,
    },
    {
      title: "Date of Registration",
      dataIndex: "createdAt",
      key: "date",
      render: (createdAt) => moment(createdAt).format("DD-MM-YYYY"),
      width: 250,
    },

    {
      title: "City",
      dataIndex: "city",
      key: "city",
      width: 250,
    },
    {
      title: "Logo",
      dataIndex: "logo",
      key: "logo",
      width: 250,
      render: (logo) => (
        <img
          src={getsrcValue(logo)}
          style={{
            width: "50px",
            height: "50px",
            marginTop: "2%",
            marginBottom: "5%",
          }}
          alt="logo"
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <Actions
          isEdit={true}
          isDelete={true}
          onClickDelete={() => {
            setDeleteId(record._id);
            setShowDelete(true);
          }}
          onClickEdit={() =>
            navigate(`/rp-qr-ratings/edit-pharmacy-partner-id/${record._id}`)
          }
        />
      ),
    },
  ];

  const dataSource = records.map((el) => ({ ...el, key: el._id }));

  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <ListPageLayout
      breadcrumbs={
        <AntdBreadcrumbs
          breadcrumbsList={[
            {
              text: "RP QR ratings",
              link: "/rp-qr-ratings/pharmacies-partner-id",
            },
            { text: "Pharmacies Partner ID", link: "" },
          ]}
        />
      }
      addButton={<FilledButton props={addNewPharmacyPartnerID} />}
      titles={<></>}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          setFilterShow={() => {}}
          showFilter={false}
        />
      }
      tableProps={{
        columns,
        dataSource,
        className: "mx-3 pt-2 table-scrollable medicineList_table",
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
      loader={loader}
    >
      {/* Optional children like modals */}
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}
    </ListPageLayout>
  );
};

export default PharmaciesPartnerID;
